import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';

// TikTok icon component (not in lucide-react)
const TikTok = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const { branding } = useBranding();
  
  const footerLinks = {
    Product: [
      { name: 'Features', href: '/treatments' },
      { name: 'Memberships', href: '/memberships' },
      { name: 'Pricing', href: '/memberships' },
      { name: 'Book Now', href: '/booking' }
    ],
    Services: [
      { name: 'Treatments', href: '/treatments' },
      { name: 'Skincare', href: '/treatments' },
      { name: 'Wellness', href: '/treatments' },
      { name: 'Consultations', href: '/booking' }
    ],
    Resources: [
      { name: 'Help Center', href: '/help' },
      { name: 'FAQs', href: '/help' },
      { name: 'Contact Us', href: '/help' }
    ],
    Company: [
      { name: 'About Us', href: '/help' },
      { name: 'Contact', href: '/help' }
    ]
  };

  // Check if any social links are configured
  const hasSocialLinks = branding.facebookUrl || branding.instagramUrl || 
                         branding.twitterUrl || branding.tiktokUrl || 
                         branding.youtubeUrl || branding.linkedinUrl;

  return (
    <footer className="bg-gray-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-8">
          {/* Logo and Description */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={branding.practiceName} className="h-10 w-auto" />
              ) : (
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-gray-900 font-bold text-xl">{branding.practiceName?.charAt(0) || 'M'}</span>
                </div>
              )}
              <span className="text-xl font-semibold text-gray-900">{branding.practiceName}</span>
            </Link>
            <p className="text-sm text-gray-600 mb-4">
              {branding.description}
            </p>
            {/* Social Links - only show if configured */}
            {hasSocialLinks && (
              <div className="flex space-x-4">
                {branding.facebookUrl && (
                  <a 
                    href={branding.facebookUrl} 
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-gray-400 hover:text-primary transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                )}
                {branding.instagramUrl && (
                  <a 
                    href={branding.instagramUrl} 
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-gray-400 hover:text-primary transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
                {branding.tiktokUrl && (
                  <a 
                    href={branding.tiktokUrl} 
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-gray-400 hover:text-primary transition-colors"
                    aria-label="TikTok"
                  >
                    <TikTok className="h-5 w-5" />
                  </a>
                )}
                {branding.youtubeUrl && (
                  <a 
                    href={branding.youtubeUrl} 
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-gray-400 hover:text-primary transition-colors"
                    aria-label="YouTube"
                  >
                    <Youtube className="h-5 w-5" />
                  </a>
                )}
                {branding.twitterUrl && (
                  <a 
                    href={branding.twitterUrl} 
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-gray-400 hover:text-primary transition-colors"
                    aria-label="Twitter"
                  >
                    <Twitter className="h-5 w-5" />
                  </a>
                )}
                {branding.linkedinUrl && (
                  <a 
                    href={branding.linkedinUrl} 
                    target="_blank"
                    rel="noopener noreferrer" 
                    className="text-gray-400 hover:text-primary transition-colors"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="h-5 w-5" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Footer Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">{category}</h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-sm text-gray-600 hover:text-primary transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} {branding.practiceName}. All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/help" className="text-sm text-gray-600 hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <Link to="/help" className="text-sm text-gray-600 hover:text-primary transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
